import * as PIXI from 'pixi.js';

/**
 * Piano keyboard layout constants.
 * Maps MIDI pitches 21 (A0) through 108 (C8) to visual keys.
 */

// Which semitones within an octave are black keys (C#=1, D#=3, F#=6, G#=8, A#=10)
const BLACK_KEY_SEMITONES = new Set([1, 3, 6, 8, 10]);

// Total white keys on 88-key piano: 52
const TOTAL_WHITE_KEYS = 52;

export interface KeyRect {
  x: number;
  y: number;
  width: number;
  height: number;
  isBlack: boolean;
  midi: number;
}

export class PianoKeyboard {
  private container: PIXI.Container;
  private keyRects: Map<number, KeyRect> = new Map();
  private keyGraphics: Map<number, PIXI.Graphics> = new Map();
  private pressedKeys: Set<number> = new Set();

  // Layout
  private x = 0;
  private y = 0;
  private totalWidth = 0;
  private whiteKeyHeight = 0;
  private blackKeyHeight = 0;
  private whiteKeyWidth = 0;

  constructor(private parentContainer: PIXI.Container) {
    this.container = new PIXI.Container();
    this.parentContainer.addChild(this.container);
  }

  /**
   * Compute layout and draw all 88 keys.
   */
  draw(x: number, y: number, width: number, height: number) {
    this.x = x;
    this.y = y;
    this.totalWidth = width;
    this.whiteKeyHeight = height;
    this.blackKeyHeight = height * 0.6;
    this.whiteKeyWidth = width / TOTAL_WHITE_KEYS;

    this.container.removeChildren();
    this.keyRects.clear();
    this.keyGraphics.clear();

    this.computeKeyRects();
    this.drawWhiteKeys();
    this.drawBlackKeys();
  }

  private computeKeyRects() {
    let whiteIndex = 0;

    for (let midi = 21; midi <= 108; midi++) {
      const semitone = midi % 12;
      const isBlack = BLACK_KEY_SEMITONES.has(semitone);

      if (isBlack) {
        // Black key centered on the boundary between adjacent white keys
        const bw = this.whiteKeyWidth * 0.6;
        const bx = this.x + whiteIndex * this.whiteKeyWidth - bw / 2;
        this.keyRects.set(midi, {
          x: bx,
          y: this.y,
          width: bw,
          height: this.blackKeyHeight,
          isBlack: true,
          midi,
        });
      } else {
        this.keyRects.set(midi, {
          x: this.x + whiteIndex * this.whiteKeyWidth,
          y: this.y,
          width: this.whiteKeyWidth,
          height: this.whiteKeyHeight,
          isBlack: false,
          midi,
        });
        whiteIndex++;
      }
    }
  }

  private drawWhiteKeys() {
    for (const [midi, rect] of this.keyRects) {
      if (rect.isBlack) continue;

      const g = new PIXI.Graphics();
      g.rect(rect.x, rect.y, rect.width, rect.height);
      g.fill(0xffffff);
      g.stroke({ width: 1, color: 0xbbbbbb });
      this.container.addChild(g);
      this.keyGraphics.set(midi, g);
    }
  }

  private drawBlackKeys() {
    for (const [midi, rect] of this.keyRects) {
      if (!rect.isBlack) continue;

      const g = new PIXI.Graphics();
      g.roundRect(rect.x, rect.y, rect.width, rect.height, 2);
      g.fill(0x222222);
      this.container.addChild(g);
      this.keyGraphics.set(midi, g);
    }
  }

  /**
   * Get the center X and width for a given MIDI pitch (for note bar positioning).
   */
  getKeyPosition(midi: number): { centerX: number; width: number } | null {
    const rect = this.keyRects.get(midi);
    if (!rect) return null;
    return {
      centerX: rect.x + rect.width / 2,
      width: rect.width,
    };
  }

  /**
   * Get the top Y of the keyboard (hit zone Y).
   */
  getTopY(): number {
    return this.y;
  }

  /**
   * Highlight a key as pressed.
   */
  pressKey(midi: number) {
    if (this.pressedKeys.has(midi)) return;
    this.pressedKeys.add(midi);

    const g = this.keyGraphics.get(midi);
    const rect = this.keyRects.get(midi);
    if (!g || !rect) return;

    g.clear();
    if (rect.isBlack) {
      g.roundRect(rect.x, rect.y, rect.width, rect.height, 2);
      g.fill(0x4488ff);
    } else {
      g.rect(rect.x, rect.y, rect.width, rect.height);
      g.fill(0xccddff);
      g.stroke({ width: 1, color: 0xbbbbbb });
    }
  }

  /**
   * Release a key highlight.
   */
  releaseKey(midi: number) {
    if (!this.pressedKeys.has(midi)) return;
    this.pressedKeys.delete(midi);

    const g = this.keyGraphics.get(midi);
    const rect = this.keyRects.get(midi);
    if (!g || !rect) return;

    g.clear();
    if (rect.isBlack) {
      g.roundRect(rect.x, rect.y, rect.width, rect.height, 2);
      g.fill(0x222222);
    } else {
      g.rect(rect.x, rect.y, rect.width, rect.height);
      g.fill(0xffffff);
      g.stroke({ width: 1, color: 0xbbbbbb });
    }
  }

  releaseAll() {
    for (const midi of [...this.pressedKeys]) {
      this.releaseKey(midi);
    }
  }

  destroy() {
    this.container.destroy({ children: true });
  }
}
